import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Phone, ShieldCheck, Award, Heart, Wrench, Users, MapPin, Clock, CheckCircle } from 'lucide-react';
import { BUSINESS, SERVICES } from '@/lib/businessData';
import Seo from '@/components/Seo';

const TIMELINE = [
  { year: '2008', title: 'The First Truck', text: 'Mike started with one truck, one toolbox, and a commitment to honest garage door repair in Cypress, TX.' },
  { year: '2012', title: 'LiftMaster ProVantage Dealer', text: 'Became an authorized LiftMaster dealer, bringing professional-grade openers to Cypress homeowners.' },
  { year: '2016', title: '24/7 Emergency Service', text: 'Launched round-the-clock emergency dispatch — because garage doors don\'t wait for business hours.' },
  { year: '2020', title: '5,000th Repair', text: 'Crossed 5,000 completed repairs across every Cypress neighborhood and surrounding community.' },
  { year: '2026', title: 'Still Family-Owned', text: 'Now a team of certified technicians serving all of Cypress — still answering the phone personally.' },
];

const CERTS = [
  { icon: ShieldCheck, title: 'Licensed & Insured', detail: `Texas contractor license ${BUSINESS.license}. Fully insured with $2M general liability coverage.` },
  { icon: Award, title: 'LiftMaster ProVantage', detail: 'Authorized dealer and certified installer for LiftMaster professional-grade openers and smart-home systems.' },
  { icon: Wrench, title: 'IDEA Certified Technicians', detail: 'Institute of Door Dealer Education and Accreditation certified — the gold standard in garage door training.' },
  { icon: ShieldCheck, title: 'Background-Checked Crew', detail: 'Every technician is background-checked, uniformed, and trained in safe spring handling protocols.' },
];

export default function AboutMike() {
  return (
    <>
      <Seo
        title="About Mike | 18 Years of Honest Garage Door Repair in Cypress, TX"
        description="Meet Mike — locally owned and operated in Cypress, TX since 2008. Licensed, insured, LiftMaster ProVantage certified, and committed to honest, same-day garage door repair."
        path="/about-mike"
      />
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-background border-b border-border tech-grid relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/80" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-4">
                  / About Mike's Garage Door Repair
                </div>
                <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-foreground text-balance leading-[0.95]">
                  18 Years. <span className="text-primary">One Mission.</span><br />Honest Garage Door Repair in Cypress.
                </h1>
                <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
                  Mike started this company in 2008 with a single truck and a simple promise: show up fast,
                  charge fair, and fix it right the first time. Nearly two decades later, that promise hasn't changed —
                  only the number of Cypress families we serve has.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center justify-center gap-2 bg-primary text-background px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:amber-glow transition-all">
                    <Phone className="w-5 h-5" />
                    {BUSINESS.phone}
                  </a>
                  <a href="/#booking" className="flex items-center justify-center gap-2 border-2 border-border text-foreground px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:border-primary hover:text-primary transition-all">
                    Book Service
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://media.base44.com/images/public/6a5847080763d214450cfad8/96af05051_generated_ee7f2ed8.png"
                  alt="Garage door installation by Mike's Garage Door Repair in Cypress TX"
                  className="w-full h-80 lg:h-96 object-cover border border-border"
                />
                <div className="absolute -bottom-4 -left-4 bg-primary text-background px-6 py-3">
                  <div className="font-heading font-black text-2xl">{BUSINESS.yearsInBusiness} Years</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider">Serving Cypress TX</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-card border-b border-border py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: '5,200+', label: 'Repairs Completed' },
                { value: BUSINESS.rating, label: 'Google Rating' },
                { value: BUSINESS.reviewCount, label: 'Verified Reviews' },
                { value: '24/7', label: 'Emergency Service' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="font-heading font-black text-3xl lg:text-4xl text-primary">{stat.value}</div>
                  <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Company History Timeline */}
        <section className="bg-background py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Our Story</div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-foreground text-balance">
                From One Truck to Cypress's Trusted Name
              </h2>
            </div>
            <div className="relative pl-8 border-l-2 border-primary/30 space-y-10">
              {TIMELINE.map((item) => (
                <div key={item.year} className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 bg-primary border-4 border-background rounded-full" />
                  <div className="font-heading font-black text-2xl text-primary mb-1">{item.year}</div>
                  <h3 className="font-heading font-bold text-lg text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Safety Certifications */}
        <section className="bg-card border-y border-border py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Safety & Certification</div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-foreground text-balance">
                Certified, Insured & Background-Checked
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Garage door springs hold enough tension to cause serious injury. That's why every technician on
                our team is trained, certified, and follows strict safety protocols on every job.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {CERTS.map((cert) => {
                const Icon = cert.icon;
                return (
                  <div key={cert.title} className="bg-background border border-border p-6 flex gap-4">
                    <div className="w-12 h-12 bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-sm text-foreground mb-1">{cert.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{cert.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Community Commitment */}
        <section className="bg-background py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-16 bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-primary" />
            </div>
            <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Community</div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-foreground text-balance mb-6">
              Proudly Serving Cypress Since Day One
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Mike lives in Cypress. His kids go to school in Cypress. When you call Mike's Garage Door Repair,
              you're not calling a national chain or a call center — you're calling a neighbor. We sponsor local
              Little League teams, donate to Cypress-area food banks, and offer senior discounts across every
              neighborhood we serve. Your support keeps Cypress local.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                { icon: Users, label: 'Local Sponsorships', detail: 'Cypress Little League & CFISD programs' },
                { icon: Heart, label: 'Senior Discounts', detail: '10% off for Cypress seniors 65+' },
                { icon: MapPin, label: 'Locally Owned', detail: 'Cypress residents serving Cypress' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="bg-card border border-border p-5">
                    <Icon className="w-6 h-6 text-primary mx-auto mb-3" />
                    <div className="font-heading font-bold text-sm text-foreground mb-1">{item.label}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{item.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary text-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading font-black text-2xl sm:text-3xl">Ready to Work with Mike's Team?</h2>
              <p className="font-mono text-sm opacity-80 mt-1">Same-day service across all of Cypress, TX.</p>
            </div>
            <div className="flex gap-3">
              <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-2 bg-background text-primary px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-opacity">
                <Phone className="w-5 h-5" />
                {BUSINESS.phone}
              </a>
              <a href="/#booking" className="flex items-center gap-2 border-2 border-background text-background px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:bg-background hover:text-primary transition-all">
                Book Online
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}